import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBackCircleOutline, IoChevronDown } from 'react-icons/io5';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

// Two amenity preview images for the slider
const AMENITY_IMAGES = [
  "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Fresidential1.jpg?alt=media&token=e4f1d0a5-1d30-4fcb-acc8-3798d173793b",
  "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Fresidential2.jpg?alt=media&token=9ad27eb3-10ae-40f3-917c-843d66ef4481"
];

// Caption for each slide
const SLIDE_CAPTIONS = [
  "Easy to Use",
  "Decorate Safely"
];

// ---------------------------------------------------------------
// Plan caps & values (from Section 3 & 9 – bulk caps identical to retail)
// ---------------------------------------------------------------
const PLAN_CAPS = {
  Starter: { images: 100, videos: 3 },
  Creator: { images: 415, videos: 15 },
  Pro:     { images: 963, videos: 35 },
};

const FREELANCE_RATE_IMAGE = 35;
const FREELANCE_RATE_VIDEO = 50;
const SOCIAL_MIX_IMAGES = 15;
const SOCIAL_MIX_VIDEOS = 3;
const MONTHLY_SOCIAL_BUDGET = SOCIAL_MIX_IMAGES * FREELANCE_RATE_IMAGE + SOCIAL_MIX_VIDEOS * FREELANCE_RATE_VIDEO; // 675

function getContentMonths(planName) {
  const caps = PLAN_CAPS[planName];
  if (!caps) return 1;
  const imgMonths = Math.floor(caps.images / SOCIAL_MIX_IMAGES);
  const vidMonths = Math.floor(caps.videos / SOCIAL_MIX_VIDEOS);
  return Math.min(imgMonths, vidMonths);
}

function getContentMonthsDescription(planName) {
  const months = getContentMonths(planName);
  if (planName === 'Starter') return '1 month of full social content (trial)';
  if (months >= 12) return '12+ months of daily/weekly posting without re-using assets';
  return `${months} months of daily/weekly posting without re-using assets`;
}

const starterFeatures = [
  "3-day free trial",
  "Generate up to 100 iterations\nOR 3 videos (7-15s each)",
  "HD resolution exports (1080p)",
  "Basic style suggestions",
  "No watermark on exports",
  "Save up to 5 design projects",
  "Video export (H.264, .MOV)",
  "Email support within 48 hours"
];

const creatorExclusiveFeatures = [
  "Generate up to 415 iterations\nOR 15 videos (7-15s each) per month",
  "4K resolution exports",
  "Advanced style suggestions",
  "Precision perspective matching",
  "Commercial usage rights",
  "3D model import capabilities",
  "Unlimited design projects",
  "Video export (H.264, .MOV, ProRes)",
  "Priority email support within 24 hours",
  "Usage limits reset each billing cycle"
];

const proExclusiveFeatures = [
  "Generate up to 963 iterations\nOR 35 videos (7-15s each) per month",
  "8K resolution exports",
  "Advanced 3D model manipulation",
  "Batch export processing",
  "Client presentation tools",
  "Dedicated priority support",
  "Early access to new features",
  "Personalized recommendations",
  "Video export (H.264, .MOV, ProRes, RAW)"
];

// ---------- BULK AMENITY PRICING (default 5‑20 seat tier) ----------
const plans = {
  monthly: [
    {
      name: "Creator",
      desc: "For tenants decorating their apartment with advanced Interior Design tools.",
      price: "$36",
      features: [...creatorExclusiveFeatures],
      cta: "Choose Creator",
      planType: "creator",
      billingCycle: "monthly",
      note: "Per seat / month (5–20 seats)"
    },
    {
      name: "Pro",
      desc: "For tenants who want the ultimate apartment decorating experience with pro-level tools.",
      price: "$80",
      features: ["All previous plan features", ...proExclusiveFeatures],
      cta: "Choose Pro",
      planType: "pro",
      billingCycle: "monthly",
      note: "Per seat / month (5–20 seats)"
    }
  ],
  annual: [
    {
      name: "Creator",
      desc: "For tenants decorating their apartment with advanced Interior Design tools.",
      price: "$382.50",
      saving: "$49.50",
      features: [...creatorExclusiveFeatures],
      cta: "Choose Creator",
      planType: "creator",
      billingCycle: "annual",
      note: "Per seat / year (5–20 seats)"
    },
    {
      name: "Pro",
      desc: "For tenants who want the ultimate apartment decorating experience with pro-level tools.",
      price: "$850",
      saving: "$110",
      features: ["All previous plan features", ...proExclusiveFeatures],
      cta: "Choose Pro",
      planType: "pro",
      billingCycle: "annual",
      note: "Per seat / year (5–20 seats)"
    }
  ]
};

const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const getFlipContent = (plan, billingCycle) => {
  const caps = PLAN_CAPS[plan.name];
  const months = getContentMonths(plan.name);
  const monthlyValue = months * MONTHLY_SOCIAL_BUDGET;
  const displayVal = billingCycle === 'annual' ? monthlyValue * 12 : monthlyValue;
  const suffix = billingCycle === 'annual' ? '/year' : '/month';
  const monthsDesc = getContentMonthsDescription(plan.name);
  const priceNum = parseFloat(plan.price.replace(/[$,]/g, ''));
  const roi = priceNum > 0 ? Math.round(displayVal / priceNum) : null;
  return { displayVal, suffix, monthsDesc, months, caps, roi };
};
// ---------------------------------------------------------------

// Accordion data for "What This Means for You"
const whatItMeansItems = [
  {
    title: "Direct savings on every seat",
    content: "You purchase plans at a discount of up to 30% off the standard retail price. For 5–20 seats, Creator is $36/seat/month (save $9/seat) and Pro is $80/seat/month (save $20/seat). At scale, those savings compound quickly."
  },
  {
    title: "Zero management overhead",
        content: <>Outlne handles provisioning, platform, and support for every seat. No support tickets, no extra work on your end, just a pure amenity value you can pass on to tenants or resell at a margin.<br /><br /><span style={{ display: 'block', textAlign: 'center', fontSize: '0.7rem', lineHeight: '1.3' }}><i>(Resale terms are subject to separate commercial terms.)</i></span></>
  },
  {
    title: "A true amenity that boosts retention",
    content: "Unlike a one-time move-in perk, this delivers ongoing value month after month. Tenants who invest time personalizing their space become emotionally attached, leading to longer stays, fewer vacancies, and a more stable property."
  },
  {
    title: "Annual plans deepen the value",
    content: "Creator annual seats are $382.50 (save $49.50/seat vs monthly) and Pro annual seats are $850 (save $110/seat). Even after the discount, every seat carries the full 415 images / 15 videos (Creator) or 963 images / 35 videos (Pro) generation budget."
  }
];

export default function ResidentialAmenity() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [gridOpacity, setGridOpacity] = useState(0.3);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [openAccordion, setOpenAccordion] = useState([false, false, false, false]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [expandedPlanIndex, setExpandedPlanIndex] = useState(null);

  const lastGridChangeTime = useRef(0);
  const slideshowTimer = useRef(null);

  useEffect(() => {
    document.title = "Residential Amenity | Outlne";
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Grid animation
  useEffect(() => {
    if (!pageLoaded) return;
    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;
    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      const timeSinceChange = time - lastGridChangeTime.current;
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      animationFrame = requestAnimationFrame(animateScroll);
    };
    animationFrame = requestAnimationFrame(animateScroll);
    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid(prev => (prev + 1) % 3);
    }, 4000);
    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Auto‑slideshow for the amenity preview
  useEffect(() => {
    slideshowTimer.current = setInterval(() => {
      setActiveImageIndex(prev => (prev + 1) % AMENITY_IMAGES.length);
    }, 4000);
    return () => {
      if (slideshowTimer.current) clearInterval(slideshowTimer.current);
    };
  }, []);

  // Manual dot click resets the timer
  const handleDotClick = (index) => {
    setActiveImageIndex(index);
    if (slideshowTimer.current) clearInterval(slideshowTimer.current);
    slideshowTimer.current = setInterval(() => {
      setActiveImageIndex(prev => (prev + 1) % AMENITY_IMAGES.length);
    }, 4000);
  };

  const gridStyles = [
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`, backgroundSize: '40px 40px', display: activeGrid === 0 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`, backgroundSize: '50px 40px', backgroundPositionX: '20px', display: activeGrid === 1 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`, backgroundSize: '40px 30px', backgroundPositionY: '10px', display: activeGrid === 2 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
  ];

  const toggleFlip = (idx) => setFlippedIndex(prev => prev === idx ? null : idx);

  const handlePlanClick = (plan) => {
    sessionStorage.setItem('selectedPlan', JSON.stringify({
      name: plan.name,
      price: plan.price,
      planType: plan.planType,
      billingCycle: plan.billingCycle,
      saving: plan.saving || null,
      isAmenity: true,
      note: plan.note || null
    }));
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const popup = window.open(
      '/auth?audience=residential',
      'Outlne Sign Up',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    if (popup) popup.focus();
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(prev => prev.map((v, i) => i === index ? !v : v));
  };

  const togglePlanExpansion = (index) => {
    setExpandedPlanIndex(prev => prev === index ? null : index);
  };

  // Responsive typography system - base sizes that scale properly
  const getResponsiveFontSize = (baseMobile, baseDesktop) => {
    return isMobile ? baseMobile : isTablet ? `${(parseFloat(baseMobile) + parseFloat(baseDesktop)) / 2}rem` : baseDesktop;
  };

  const typography = {
    // Paragraph text - the main issue
    bodyText: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1.05rem' : '1.125rem',
      lineHeight: isMobile ? '1.65' : '1.7',
    },
    // Large intro paragraphs
    introText: {
      fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
      lineHeight: isMobile ? '1.6' : '1.6',
    },
    // Section headings
    sectionHeading: {
      fontSize: isMobile ? '1.75rem' : isTablet ? '2rem' : '2rem',
      lineHeight: '1.2',
    },
    // Main title
    mainTitle: {
      fontSize: isMobile ? '1.75rem' : isTablet ? '2.5rem' : '3.5rem',
      lineHeight: '1.1',
    },
    // Card descriptions
    cardDesc: {
      fontSize: isMobile ? '0.85rem' : isTablet ? '0.9rem' : '1rem',
      lineHeight: '1.5',
    },
    // Feature list items
    featureItem: {
      fontSize: isMobile ? '0.75rem' : isTablet ? '0.85rem' : '0.95rem',
      lineHeight: '1.4',
    },
    // Accordion content
    accordionContent: {
      fontSize: isMobile ? '0.85rem' : isTablet ? '0.9rem' : '0.95rem',
      lineHeight: '1.7',
    },
    // Accordion title
    accordionTitle: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1rem' : '1.1rem',
    },
    // Flip card content
    flipContent: {
      fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '0.95rem',
      lineHeight: '1.5',
    },
    // Flip card value
    flipValue: {
      fontSize: isMobile ? '1.2rem' : isTablet ? '1.35rem' : '1.5rem',
      lineHeight: '1.2',
    },
    // Note text
    noteText: {
      fontSize: isMobile ? '0.75rem' : isTablet ? '0.8rem' : '0.85rem',
      lineHeight: '1.6',
    },
    // Centered emphasis text
    emphasisText: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1rem' : '1rem',
      lineHeight: '1.7',
    },
  };

  const centeredBlockStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    paddingLeft: isMobile || isTablet ? '15px' : '0',
    paddingRight: isMobile || isTablet ? '15px' : '0'
  };

  const accordionContainerStyle = {
    ...centeredBlockStyle,
    marginTop: '2rem',
    marginBottom: '2rem'
  };

  const infoIconStyle = {
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 'bold',
    color: 'white',
    lineHeight: 1,
    marginLeft: '8px',
    verticalAlign: 'middle',
    transition: 'background-color 0.2s ease',
  };

  return (
    <>
      {/* Black background */}
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(29, 29, 29, 0.8)', zIndex: -3 }} />

      {/* Grid overlay */}
      <div style={{
        position: 'fixed', top: 0, left: 0, width: isMobile ? '100vw' : '50vw', bottom: 0,
        zIndex: -1, overflow: 'hidden', opacity: gridOpacity, transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute', inset: 0, width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, idx) => (
            <div key={idx} style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', ...style }} />
          ))}
        </div>
      </div>

      {/* Back button – desktop/tablet */}
      {!isMobile && (
        <div
          onClick={() => navigate('/residential')}
          style={{
            position: 'fixed', top: '30px', left: '30px',
            zIndex: 30, cursor: 'pointer',
            color: 'white', fontSize: '2rem',
            transition: 'opacity 0.2s ease',
          }}
          onMouseOver={e => e.currentTarget.style.opacity = 0.7}
          onMouseOut={e => e.currentTarget.style.opacity = 1}
          title="Back to Residential"
        >
          <IoArrowBackCircleOutline />
        </div>
      )}

      {/* Header */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: '40px', right: '40px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: 'auto' }} />
            <h1 style={{ fontSize: '1.8rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
          </div>
          <p style={{ fontSize: '1rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
            Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 8px', borderRadius: '4px', display: 'inline-block', minWidth: '60px', textAlign: 'center' }}>Editor</span>
          </p>
        </div>
      )}
      {isMobile && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '110px', backgroundColor: 'rgba(31, 31, 31, 0.8)', backdropFilter: 'blur(10px)', zIndex: 15 }} />
          <div style={{ position: 'fixed', top: '20px', left: '20px', zIndex: 20, textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
            <p style={{ fontSize: '0.9rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', minWidth: '50px', textAlign: 'center' }}>Editor</span>
            </p>
          </div>
          <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: '40px' }} />
            <div
              onClick={() => navigate('/residential')}
              style={{ cursor: 'pointer', marginRight: '5px', fontSize: '1.8rem', color: 'white', marginTop: '2px' }}
            >
              <IoArrowBackCircleOutline />
            </div>
          </div>
        </>
      )}

      {/* Main content */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5,
        overflowY: 'auto', overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0, transition: 'opacity 1s ease',
        color: 'white', fontFamily: 'avenir-lt-pro, sans-serif',
      }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto',
          padding: isMobile ? '120px 24px 40px' : isTablet ? '140px 32px 60px' : '160px 40px 80px'
        }}>

          {/* Title */}
          <h1 style={{
            fontFamily: 'didot, sans-serif',
            fontStyle: 'italic',
            fontSize: typography.mainTitle.fontSize,
            fontWeight: 400, 
            marginBottom: '2rem',
            lineHeight: typography.mainTitle.lineHeight, 
            letterSpacing: '-0.5px', 
            textAlign: 'center',
            paddingLeft: isMobile || isTablet ? '15px' : '0',
            paddingRight: isMobile || isTablet ? '15px' : '0',
          }}>
            A Resident Amenity<br />that pays for itself
          </h1>

          {/* Intro text with image carousel */}
          <div style={{
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: '40px', 
            alignItems: 'center',
            marginBottom: '4rem',
            textAlign: 'center',
            paddingLeft: isMobile || isTablet ? '15px' : '0',
            paddingRight: isMobile || isTablet ? '15px' : '0',
          }}>
            <div style={{ flex: 1 }}>
              {/* Intro paragraphs: center on mobile, left on desktop */}
              <p style={{
                fontSize: typography.introText.fontSize, 
                lineHeight: typography.introText.lineHeight, 
                color: '#ccc',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                When a tenant enters into a lease for a new place, it's an exciting aspirational moment, a fresh space, full of possibility.
              </p>
              <p style={{
                fontSize: typography.introText.fontSize, 
                lineHeight: typography.introText.lineHeight, 
                color: '#ccc',
                marginTop: '1rem',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                That excitement is real, and transforming a blank canvas into a home means navigating apartment-friendly changes, getting landlord approval, and planning carefully before committing to anything. It's a process that takes time and thought.
              </p>
            </div>

            {/* Image carousel with auto‑slideshow and two dots */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? '100%' : 'auto'
            }}>
              <div style={{
                border: '3px solid white',
                borderRadius: '28px',
                overflow: 'hidden',
                width: isMobile ? 'auto' : '100%',
                maxWidth: isMobile ? 'none' : '280px',
                height: isMobile ? '450px' : 'auto',
                aspectRatio: '9 / 16',
                position: 'relative',
                backgroundColor: '#1a1a1a'
              }}>
                {/* Shimmer skeleton placeholder */}
                {AMENITY_IMAGES.map((_, idx) => (
                  !imagesLoaded[idx] && (
                    <div
                      key={`shimmer-${idx}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite linear',
                        zIndex: 1,
                        transition: 'opacity 0.4s ease',
                        opacity: idx === activeImageIndex ? 1 : 0,
                      }}
                    />
                  )
                ))}
                {AMENITY_IMAGES.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`Amenity preview ${idx + 1}`}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setImagesLoaded(prev => ({ ...prev, [idx]: true }))}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'opacity 0.6s ease',
                      opacity: idx === activeImageIndex ? 1 : 0,
                    }}
                  />
                ))}
                <div style={{
                  position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(203, 203, 203, 0.25)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                   color: 'white', padding: '6px 20px', borderRadius: '20px',
                  fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap'
                }}>
                  {SLIDE_CAPTIONS[activeImageIndex]}
                </div>
              </div>

              {/* Circle navigation dots */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '16px',
                justifyContent: 'center',
              }}>
                {AMENITY_IMAGES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDotClick(idx)}
                    aria-label={`Image ${idx + 1}`}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: idx === activeImageIndex ? '#408aff' : '#555',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* More intro (centered) */}
          <div style={centeredBlockStyle}>
            <p style={{
              fontSize: typography.bodyText.fontSize,
              lineHeight: typography.bodyText.lineHeight,
              marginBottom: '1.5rem', 
              color: '#ccc',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Offering Outlne as an additional amenity gives tenants the ability to request approval and decorate safely: preview furniture, try different paint colors and creative apartment friendly layouts around their space, before any physical changes are made.
            </p>
            <p style={{
              fontSize: typography.bodyText.fontSize,
              lineHeight: typography.bodyText.lineHeight,
              marginBottom: '1.5rem', 
              color: '#ccc',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              So when the conversation about making rental changes happens, they're coming to you with a clear visual plan, not a vague, risky idea. That makes the process safer and easier for everyone, while naturally increasing retention, because tenants who've invested time in personalizing their home feel far more attached and are likely to stay long term.
            </p>
            <p style={{
              fontSize: typography.emphasisText.fontSize,
              lineHeight: typography.emphasisText.lineHeight,
              marginBottom: '2rem', 
              color: '#ccc',
              textAlign: 'center', 
              fontWeight: 500
            }}>
              Apartment-friendly planning for your units made safe and easy.
            </p>
          </div>

          {/* White separator */}
          <hr style={{
            border: 'none', borderTop: '1px solid rgba(255,255,255,0.25)',
            margin: '3rem auto', maxWidth: '300px',
          }} />

          {/* How the Subscription works – Pricing cards */}
          <section style={{ margin: '4rem 0' }}>
            <h2 style={{ 
              fontFamily: 'didot, sans-serif',
              fontStyle: 'italic',
              fontSize: typography.sectionHeading.fontSize, 
              fontWeight: 400, 
              marginBottom: '2rem', 
              textAlign: 'center' 
            }}>
              How the Subscription Works
            </h2>

            {/* Billing toggle */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '2rem', padding: '0.5rem',
              backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '50px',
              border: '1px solid rgba(255,255,255,0.1)', maxWidth: '300px', margin: '0 auto 2.5rem',
            }}>
              <span onClick={() => setBillingCycle('monthly')} style={{
                color: billingCycle === 'monthly' ? 'white' : '#888',
                padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'monthly' ? 600 : 400, cursor: 'pointer'
              }}>Monthly</span>
              <div onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')} style={{
                width: '40px', height: '24px', borderRadius: '12px',
                backgroundColor: billingCycle === 'monthly' ? '#333' : '#40a3ff',
                position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px', transition: 'all 0.3s ease'
              }}>
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', position: 'absolute', left: billingCycle === 'monthly' ? '2px' : '18px', transition: 'left 0.3s ease' }} />
              </div>
              <span onClick={() => setBillingCycle('annual')} style={{
                color: billingCycle === 'annual' ? 'white' : '#888',
                padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'annual' ? 600 : 400, cursor: 'pointer'
              }}>Annual</span>
            </div>

            {/* Cards – accordion on mobile, side-by-side on desktop */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              gap: isMobile ? '1rem' : '2rem',
              flexWrap: 'nowrap',
              paddingLeft: isMobile ? '10px' : '0',
              paddingRight: isMobile ? '10px' : '0',
            }}>
              {plans[billingCycle].map((plan, idx) => {
                const isFlipped = flippedIndex === idx;
                const flipContent = getFlipContent(plan, billingCycle);
                const isExpanded = expandedPlanIndex === idx;

                return (
                  <div key={idx}
                    style={{
                      backgroundColor: 'rgba(47, 47, 47, 0.9)', 
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '1rem',
                      padding: isMobile ? '1rem' : '2rem',
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: isMobile ? 'auto' : '320px',
                      maxWidth: isMobile ? '100%' : '380px',
                      minWidth: 0,
                      width: isMobile ? '100%' : 'auto',
                      backdropFilter: 'blur(15px)', 
                      WebkitBackdropFilter: 'blur(15px)',
                      display: 'flex', 
                      flexDirection: 'column',
                      minHeight: isMobile ? 'auto' : '750px',
                      overflow: 'hidden',
                      transition: 'max-height 0.4s ease, min-height 0.4s ease',
                    }}
                  >
                    {/* Mobile: Clickable header for accordion */}
                    {isMobile ? (
                      <>
                        <div 
                          onClick={() => togglePlanExpansion(idx)}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <h2 style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 600, 
                            marginBottom: '0.3rem', 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              {plan.name}
                              <span
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  toggleFlip(idx); 
                                }}
                                style={infoIconStyle}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                              >i</span>
                            </span>
                            <IoChevronDown
                              style={{
                                color: '#40a3ff',
                                transition: 'transform 0.3s ease',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                fontSize: '1.4rem',
                                flexShrink: 0,
                              }}
                            />
                          </h2>
                          <p style={{ 
                            color: '#ccc', 
                            fontSize: typography.cardDesc.fontSize, 
                            marginBottom: '0.5rem', 
                            lineHeight: typography.cardDesc.lineHeight 
                          }}>{plan.desc}</p>
                        </div>

                        {/* Expandable content */}
                        <div style={{
                          maxHeight: isExpanded ? '2000px' : '0px',
                          overflow: 'hidden',
                          transition: 'max-height 0.5s ease',
                          opacity: isExpanded ? 1 : 0,
                          transitionProperty: 'max-height, opacity',
                          transitionDuration: '0.5s, 0.3s',
                          transitionDelay: isExpanded ? '0s, 0.15s' : '0s, 0s',
                        }}>
                          {/* Grid wrapper for flip cards */}
                          <div style={{ display: 'grid', gridTemplateRows: '1fr', flex: 1 }}>
                            {/* Front side */}
                            <div style={{
                              gridRow: 1, gridColumn: 1,
                              position: isFlipped ? 'absolute' : 'relative',
                              top: isFlipped ? 0 : undefined,
                              left: isFlipped ? 0 : undefined,
                              right: isFlipped ? 0 : undefined,
                              bottom: isFlipped ? 0 : undefined,
                              opacity: isFlipped ? 0 : 1,
                              pointerEvents: isFlipped ? 'none' : 'auto',
                              display: 'flex', flexDirection: 'column',
                              height: isFlipped ? undefined : '100%'
                            }}>
                              <div style={{ marginBottom: '0.8rem' }}>
                                <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: 0 }}>
                                  {plan.price}<span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>/mo</span>
                                </p>
                                {billingCycle === 'annual' && plan.saving && (
                                  <p style={{ color: '#40a3ff', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>Save {plan.saving} per seat annually</p>
                                )}
                                {billingCycle === 'annual' && (
                                  <p style={{ color: '#888', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>Billed annually at {plan.price}</p>
                                )}
                                {plan.note && (
                                  <p style={{ color: '#aaa', fontSize: '0.75rem', margin: '0.5rem 0 0' }}>{plan.note}</p>
                                )}
                              </div>
                              <ul style={{ 
                                listStyle: 'none', 
                                padding: 0, 
                                margin: '0 0 1rem', 
                                flex: 1, 
                                color: 'white', 
                                fontSize: typography.featureItem.fontSize 
                              }}>
                                {plan.features.map((feature, fIdx) => {
                                  const isHeader = feature.startsWith('All ');
                                  return (
                                    <li key={fIdx} style={{
                                      marginBottom: '0.3rem', 
                                      paddingLeft: isHeader ? '0' : '1.2rem',
                                      position: 'relative', 
                                      lineHeight: typography.featureItem.lineHeight,
                                      fontWeight: isHeader ? 600 : 400,
                                      color: isHeader ? '#40a3ff' : 'white',
                                      fontSize: isHeader ? '0.7rem' : 'inherit',
                                      textTransform: isHeader ? 'uppercase' : 'none',
                                    }}>
                                      {!isHeader && <span style={{ position: 'absolute', left: 0, color: '#40a3ff' }}>✓</span>}
                                      {feature}
                                    </li>
                                  );
                                })}
                              </ul>
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                                style={{
                                  width: '100%', padding: '10px 16px',
                                  backgroundColor: 'white', color: '#000',
                                  border: 'none', borderRadius: '50px', fontWeight: 600,
                                  fontSize: '0.9rem', cursor: 'pointer',
                                  marginTop: 'auto'
                                }}
                              >{plan.cta}</button>
                            </div>

                            {/* Back side (Real-World Value) */}
                            <div style={{
                              gridRow: 1, gridColumn: 1,
                              position: !isFlipped ? 'absolute' : 'relative',
                              top: !isFlipped ? 0 : undefined,
                              left: !isFlipped ? 0 : undefined,
                              right: !isFlipped ? 0 : undefined,
                              bottom: !isFlipped ? 0 : undefined,
                              opacity: isFlipped ? 1 : 0,
                              pointerEvents: isFlipped ? 'auto' : 'none',
                              display: 'flex', flexDirection: 'column',
                              height: !isFlipped ? undefined : '100%'
                            }}>
                              <p style={{ color: '#40a3ff', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.8rem', textTransform: 'uppercase' }}>
                                Real-World Value
                              </p>
                              <p style={{ 
                                color: '#ddd', 
                                fontSize: typography.flipContent.fontSize, 
                                lineHeight: typography.flipContent.lineHeight, 
                                margin: '0 0 1.2rem' 
                              }}>
                               Outlne lets you explore endless design iterations via manual area selection: swap furniture, paint, change materials, and more. <br></br><br></br> Once the user has perfected a room, they then export it as a final image or turn it into a short motion clip.
                        </p>
                              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '0.8rem', padding: '0.8rem', marginBottom: '1.2rem' }}>
                                <p style={{ 
                                  fontSize: typography.flipValue.fontSize, 
                                  fontWeight: 'bold', 
                                  margin: 0 
                                }}>
                                  {formatter.format(flipContent.displayVal)}<span style={{ fontSize: '0.9rem', fontWeight: 400 }}>{flipContent.suffix}</span>
                                </p>
                                <p style={{ color: '#bbb', fontSize: '0.8rem', margin: '0.3rem 0 0' }}>
                                  What a professional photographer & videographer would charge for the same final content output.
                                </p>
                                <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '0.8rem 0 0', color: '#ccc', fontSize: '0.7rem', lineHeight: 1.5 }}>
                                  <li>1 month of social content ≈ 15 final images + 3 motion clips (3–15s)</li>
                                  <li>Industry rates: $35 per final image, $50 per motion clip</li>
                                  <li style={{ marginTop: '0.3rem', color: '#40a3ff' }}>
                                    {flipContent.monthsDesc} × {formatter.format(MONTHLY_SOCIAL_BUDGET)} = {formatter.format(flipContent.displayVal)}
                                  </li>
                                </ul>
                                {flipContent.roi && (
                                  <p style={{ color: '#40a3ff', fontSize: '0.8rem', margin: '0.6rem 0 0', fontWeight: 600 }}>
                                    {flipContent.roi}× your plan cost
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                                style={{
                                  width: '100%', padding: '10px 16px',
                                  backgroundColor: 'white', color: '#000',
                                  border: 'none', borderRadius: '50px', fontWeight: 600,
                                  fontSize: '0.9rem', cursor: 'pointer',
                                  marginTop: 'auto'
                                }}
                              >{plan.cta}</button>
                              <p
                                onClick={(e) => { e.stopPropagation(); toggleFlip(idx); }}
                                style={{ color: '#40a3ff', fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.8rem', textAlign: 'center' }}
                              >Back to Plan Features</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Desktop layout - always visible */}
                        <h2 style={{ 
                          fontSize: '1.6rem', 
                          fontWeight: 600, 
                          marginBottom: '0.5rem', 
                          display: 'flex', 
                          alignItems: 'center' 
                        }}>
                          {plan.name}
                          <span
                            onClick={(e) => { e.stopPropagation(); toggleFlip(idx); }}
                            style={infoIconStyle}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                          >i</span>
                        </h2>

                        {/* Grid wrapper for flip cards */}
                        <div style={{ display: 'grid', gridTemplateRows: '1fr', flex: 1 }}>
                          {/* Front side */}
                          <div style={{
                            gridRow: 1, gridColumn: 1,
                            position: isFlipped ? 'absolute' : 'relative',
                            top: isFlipped ? 0 : undefined,
                            left: isFlipped ? 0 : undefined,
                            right: isFlipped ? 0 : undefined,
                            bottom: isFlipped ? 0 : undefined,
                            opacity: isFlipped ? 0 : 1,
                            pointerEvents: isFlipped ? 'none' : 'auto',
                            display: 'flex', flexDirection: 'column',
                            height: isFlipped ? undefined : '100%'
                          }}>
                            <p style={{ 
                              color: '#ccc', 
                              fontSize: typography.cardDesc.fontSize, 
                              marginBottom: '1.5rem', 
                              lineHeight: typography.cardDesc.lineHeight 
                            }}>{plan.desc}</p>
                            <div style={{ marginBottom: '1.5rem' }}>
                              <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>
                                {plan.price}<span style={{ fontSize: '1.1rem', fontWeight: 'normal' }}>/mo</span>
                              </p>
                              {billingCycle === 'annual' && plan.saving && (
                                <p style={{ color: '#40a3ff', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>Save {plan.saving} per seat annually</p>
                              )}
                              {billingCycle === 'annual' && (
                                <p style={{ color: '#888', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>Billed annually at {plan.price}</p>
                              )}
                              {plan.note && (
                                <p style={{ color: '#aaa', fontSize: '0.75rem', margin: '0.5rem 0 0' }}>{plan.note}</p>
                              )}
                            </div>
                            <ul style={{ 
                              listStyle: 'none', 
                              padding: 0, 
                              margin: '0 0 1rem', 
                              flex: 1, 
                              color: 'white', 
                              fontSize: typography.featureItem.fontSize,
                              whiteSpace: 'pre-line'
                            }}>
                              {plan.features.map((feature, fIdx) => {
                                const isHeader = feature.startsWith('All ');
                                return (
                                  <li key={fIdx} style={{
                                    marginBottom: '0.3rem', 
                                    paddingLeft: isHeader ? '0' : '1.2rem',
                                    position: 'relative', 
                                    lineHeight: typography.featureItem.lineHeight,
                                    fontWeight: isHeader ? 600 : 400,
                                    color: isHeader ? '#40a3ff' : 'white',
                                    fontSize: isHeader ? '0.7rem' : 'inherit',
                                    textTransform: isHeader ? 'uppercase' : 'none',
                                  }}>
                                    {!isHeader && <span style={{ position: 'absolute', left: 0, color: '#40a3ff' }}>✓</span>}
                                    {feature}
                                  </li>
                                );
                              })}
                            </ul>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                              style={{
                                width: '100%', padding: '14px 28px',
                                backgroundColor: 'white', color: '#000',
                                border: 'none', borderRadius: '50px', fontWeight: 600,
                                fontSize: '1rem', cursor: 'pointer',
                                marginTop: 'auto'
                              }}
                            >{plan.cta}</button>
                          </div>

                          {/* Back side (Real-World Value) */}
                          <div style={{
                            gridRow: 1, gridColumn: 1,
                            position: !isFlipped ? 'absolute' : 'relative',
                            top: !isFlipped ? 0 : undefined,
                            left: !isFlipped ? 0 : undefined,
                            right: !isFlipped ? 0 : undefined,
                            bottom: !isFlipped ? 0 : undefined,
                            opacity: isFlipped ? 1 : 0,
                            pointerEvents: isFlipped ? 'auto' : 'none',
                            display: 'flex', flexDirection: 'column',
                            height: !isFlipped ? undefined : '100%'
                          }}>
                            <p style={{ color: '#40a3ff', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.8rem', textTransform: 'uppercase' }}>
                              Real-World Value
                            </p>
                            <p style={{ 
                              color: '#ddd', 
                              fontSize: typography.flipContent.fontSize, 
                              lineHeight: typography.flipContent.lineHeight, 
                              margin: '0 0 1.2rem' 
                            }}>
                              Outlne lets you explore endless design iterations by area selection: swap furniture, paint and change materials, and more. <br></br><br></br> Once the user has perfected a room, they then export it as a final image or turn it into a short motion clip.
                            </p>
                            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '0.8rem', padding: '1rem', marginBottom: '1.2rem' }}>
                              <p style={{ 
                                fontSize: typography.flipValue.fontSize, 
                                fontWeight: 'bold', 
                                margin: 0 
                              }}>
                                {formatter.format(flipContent.displayVal)}<span style={{ fontSize: '0.9rem', fontWeight: 400 }}>{flipContent.suffix}</span>
                              </p>
                              <p style={{ color: '#bbb', fontSize: '0.8rem', margin: '0.3rem 0 0' }}>
                                What a professional photographer & videographer would charge for the same final content output.
                              </p>
                              <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '0.8rem 0 0', color: '#ccc', fontSize: '0.8rem', lineHeight: 1.5 }}>
                                <li>1 month of social content ≈ 15 final images + 3 motion clips (3–15s)</li>
                                <li>Industry rates: $35 per final image, $50 per motion clip</li>
                                <li style={{ marginTop: '0.3rem', color: '#40a3ff' }}>
                                  {flipContent.monthsDesc} × {formatter.format(MONTHLY_SOCIAL_BUDGET)} = {formatter.format(flipContent.displayVal)}
                                </li>
                              </ul>
                              {flipContent.roi && (
                                <p style={{ color: '#40a3ff', fontSize: '0.8rem', margin: '0.6rem 0 0', fontWeight: 600 }}>
                                  {flipContent.roi}× your plan cost
                                </p>
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                              style={{
                                width: '100%', padding: '14px 28px',
                                backgroundColor: 'white', color: '#000',
                                border: 'none', borderRadius: '50px', fontWeight: 600,
                                fontSize: '1rem', cursor: 'pointer',
                                marginTop: 'auto'
                              }}
                            >{plan.cta}</button>
                            <p
                              onClick={(e) => { e.stopPropagation(); toggleFlip(idx); }}
                              style={{ color: '#40a3ff', fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.8rem', textAlign: 'center' }}
                            >Back to Plan Features</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* What this means for you – Accordion */}
          <section style={{ margin: '4rem 0' }}>
            <h2 style={{ 
              fontFamily: 'didot, sans-serif',
              fontStyle: 'italic',
              fontSize: typography.sectionHeading.fontSize, 
              fontWeight: 400, 
              textAlign: 'center', 
              marginBottom: '2rem' 
            }}>
              What This Means for You
            </h2>
            <div style={accordionContainerStyle}>
              {whatItMeansItems.map((item, index) => (
                <div key={index} style={{ marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                  <div
                    onClick={() => toggleAccordion(index)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0',
                      cursor: 'pointer',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: typography.accordionTitle.fontSize,
                    }}
                  >
                    <span>{item.title}</span>
                    <IoChevronDown
                      style={{
                        color: '#40a3ff',
                        transition: 'transform 0.3s ease',
                        transform: openAccordion[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                        fontSize: '1.4rem',
                      }}
                    />
                  </div>
                  <div style={{
                    maxHeight: openAccordion[index] ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                  }}>
                    <p style={{
                      color: '#ccc',
                      lineHeight: typography.accordionContent.lineHeight,
                      padding: '0 0 1rem 0',
                      margin: 0,
                      fontSize: typography.accordionContent.fontSize,
                    }}>
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}
              <p style={{ 
                color: '#aaa', 
                fontSize: typography.noteText.fontSize, 
                textAlign: 'center', 
                marginTop: '2rem', 
                lineHeight: typography.noteText.lineHeight 
              }}>
                For example, a property with 80 units buying 80 Creator seats pays $2,520/month total – saving $1,080/month compared with giving each tenant the $45 retail plan.
              </p>
            </div>
          </section>

          {/* Ready to Offer */}
          <section style={{ textAlign: 'center', margin: '5rem 0 3rem' }}>
            <h2 style={{ 
              fontFamily: 'didot, sans-serif',
              fontStyle: 'italic',
              fontSize: typography.sectionHeading.fontSize, 
              fontWeight: 400, 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              Ready to Offer This to Your Tenants?
            </h2>
            <button
              onClick={() => handlePlanClick({ name: 'General Amenity Inquiry', planType: 'amenity', billingCycle: 'inquiry' })}
              style={{
                backgroundColor: '#40a3ff', border: 'none', color: 'white',
                fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.2rem', fontWeight: 500,
                padding: '14px 40px', borderRadius: '40px', cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#2f8ad6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#40a3ff'}
            >Enroll</button>
          </section>
        </div>

        <Footer />
      </div>

      <style>{`
        html, body, #root {
          overflow: hidden; height: 100%; width: 100%; position: fixed;
          margin: 0; padding: 0;
        }
        * {
          -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
          -webkit-tap-highlight-color: transparent;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </>
  );
}